/**
 * Module dependencies.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Badge } from 'app/components/ui/badge';
import { Button } from 'app/components/ui/button';
import { cn } from 'app/lib/utils';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Info,
  Radio,
  ScrollText,
  Trash2,
  XCircle
} from 'lucide-react';
import { CryptoBotLog, useClearCryptoBotLogs, useCryptoBotLogs } from 'app/hooks/requests/crypto-bot/use-crypto-bot';

/**
 * Log rows shown per page.
 */

const PAGE_SIZE = 100;

/**
 * Icon + colour per log level.
 */

function levelStyles(level: CryptoBotLog['level']): { icon: JSX.Element; text: string; dot: string } {
  switch (level) {
    case 'success':
      return { icon: <CheckCircle2 className={'h-4 w-4'} />, text: 'text-emerald-600', dot: 'bg-emerald-500' };
    case 'warning':
      return { icon: <AlertTriangle className={'h-4 w-4'} />, text: 'text-amber-600', dot: 'bg-amber-500' };
    case 'error':
      return { icon: <XCircle className={'h-4 w-4'} />, text: 'text-destructive', dot: 'bg-destructive' };
    default:
      return { icon: <Info className={'h-4 w-4'} />, text: 'text-muted-foreground', dot: 'bg-muted-foreground' };
  }
}

/**
 * Format a timestamp as a short local time.
 */

function when(value: string): string {
  const date = new Date(value.replace(' ', 'T'));

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Export `CryptoBotLogs` component.
 */

export function CryptoBotLogs(): JSX.Element {
  const [translate] = useTranslation();
  const { data, isLoading } = useCryptoBotLogs();
  const clearLogs = useClearCryptoBotLogs();
  const [page, setPage] = React.useState(0);

  const total = data?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = (data ?? []).slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  if (isLoading || !data) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-center gap-3'}>
        <Title className={'pb-0'}>{translate('cryptoBot.logs.title')}</Title>

        <Badge className={'gap-1'} variant={'outline'}>
          <Radio className={'h-3 w-3 animate-pulse text-emerald-500'} />
          {translate('cryptoBot.logs.live')}
        </Badge>

        <Button
          className={'ml-auto'}
          disabled={data.length === 0 || clearLogs.isLoading}
          onClick={() => clearLogs.mutate()}
          size={'sm'}
          variant={'outline'}
        >
          <Trash2 className={'h-4 w-4'} />
          {translate('cryptoBot.logs.clear')}
        </Button>
      </div>

      <p className={'text-sm text-muted-foreground'}>{translate('cryptoBot.logs.description')}</p>

      {data.length === 0 ? (
        <div
          className={
            'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-20 text-center'
          }
        >
          <ScrollText className={'h-8 w-8 text-muted-foreground'} />

          <div className={'text-base font-semibold text-foreground'}>{translate('cryptoBot.logs.emptyTitle')}</div>

          <p className={'max-w-md text-sm text-muted-foreground'}>{translate('cryptoBot.logs.emptyDescription')}</p>
        </div>
      ) : (
        <ol className={'space-y-1'}>
          {pageItems.map(entry => {
            const styles = levelStyles(entry.level);

            return (
              <li
                className={
                  'flex items-center gap-2 overflow-hidden rounded-lg border border-border bg-card px-3 py-1.5 text-sm shadow-sm'
                }
                key={entry.id}
              >
                <span className={cn('shrink-0', styles.text)}>{styles.icon}</span>

                <span className={'shrink-0 text-[11px] tabular-nums text-muted-foreground'}>{when(entry.at)}</span>

                <Badge className={'shrink-0 px-1.5 py-0 text-[10px] uppercase'} variant={'secondary'}>
                  {entry.event}
                </Badge>

                {entry.symbol ? (
                  <span className={'shrink-0 text-[11px] font-medium text-foreground'}>{entry.symbol}</span>
                ) : null}

                <Badge
                  className={'shrink-0 px-1.5 py-0 text-[10px] uppercase'}
                  variant={entry.mode === 'live' ? 'destructive' : 'outline'}
                >
                  {translate(`cryptoBot.mode.${entry.mode}`)}
                </Badge>

                <span className={cn('min-w-0 flex-1 truncate', styles.text)} title={entry.message}>
                  {entry.message}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {totalPages > 1 ? (
        <div className={'flex items-center justify-center gap-3 pt-1'}>
          <Button
            disabled={safePage === 0}
            onClick={() => setPage(Math.max(0, safePage - 1))}
            size={'sm'}
            variant={'outline'}
          >
            <ChevronLeft className={'h-4 w-4'} />
          </Button>

          <span className={'text-xs tabular-nums text-muted-foreground'}>
            {translate('cryptoBot.logs.page', { page: safePage + 1, total: totalPages })}
          </span>

          <Button
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            size={'sm'}
            variant={'outline'}
          >
            <ChevronRight className={'h-4 w-4'} />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
